import React, { FC } from "react";

import { CurrencyFormat, DateFormat, Status } from "@/components/atoms";
import { InsurerLinkDropdown, MultiLinesCell, SingleLineCell } from "@/components/molecules";
import { formatDueDate } from "@/core/date";
import { Contract, Permissions } from "@/shared/domain";
import { useAuth, useNavigation } from "@/shared/hooks";
import { getInsurerLinks } from "@/shared/utils";
import {
    StyledFlexRow,
    StyledTableCell,
    StyledTableCellCategoryIcon,
    StyledTableCellImg,
    StyledTableCellItem,
    StyledTableCellText,
    StyledTableRow,
} from "@/styles/styled";

import { getStatusColor } from "../../../utils";
import { ContractImage } from "../../ContractImage/ContractImage";

export type ContractTableItemProps = {
    contract: Contract;
};

const ContractTableItem: FC<ContractTableItemProps> = ({ contract }) => {
    const { user } = useAuth();
    const { navToContractDetail } = useNavigation();

    const contractCategory = contract.categories[0]?.id || "0";
    const canViewDetails = user.hasPermission(Permissions.ViewContractsDetails, contract.policyholderId);

    const handleInsuredGoodDetails = () => {
        return canViewDetails && navToContractDetail(contract);
    };

    const insurerLinks = getInsurerLinks(contract);

    return (
        <StyledTableRow
            hover={canViewDetails}
            onClick={handleInsuredGoodDetails}
            data-testid={`contract-table-item-${contract.id}`}
        >
            <MultiLinesCell
                testId="table-cell-description"
                variant1="main"
                variant2="grey"
                highlight1
                highlight2
                icon={
                    <StyledTableCellImg
                        style={{ marginRight: "16px" }}
                        src={contract.insurer.logoUrl}
                        alt={`${contract.insurer.name}${contract.insurer.fsma}`}
                    />
                }
                lines={[
                    {
                        key: contract.reference,
                        line1: contract.branch.name,
                        line2: contract.reference,
                    },
                ]}
            />

            <MultiLinesCell
                testId="table-cell-concerns"
                variant1="grey"
                highlight1
                highlight2
                lines={(contract.risks || []).map((risk) => {
                    return {
                        key: risk.id,
                        line1: risk.type.name,
                        line2: risk.name,
                    };
                })}
            />

            <SingleLineCell testId="table-cell-amount" highlight>
                <CurrencyFormat value={contract.amount} />
            </SingleLineCell>
            <SingleLineCell testId="table-cell-periodicity" highlight>
                {contract.periodicity.name}
            </SingleLineCell>
            <SingleLineCell testId="table-cell-effectiveDate" highlight>
                <DateFormat date={contract.effectiveDate} />
            </SingleLineCell>
            <SingleLineCell testId="table-cell-dueDate" highlight>
                {formatDueDate(contract.dueDate)}
            </SingleLineCell>
            <StyledTableCell data-testid="table-cell-state">
                <StyledFlexRow>
                    <div style={{ overflow: "hidden" }}>
                        <StyledTableCellItem>
                            <Status color={getStatusColor(contract.state.id)} text={contract.state.name} />
                        </StyledTableCellItem>
                        <StyledTableCellItem>
                            <StyledTableCellText $highlight>
                                <InsurerLinkDropdown
                                    insurerLinks={insurerLinks}
                                    id={contract.id}
                                    insurer={contract.insurer}
                                />
                            </StyledTableCellText>
                        </StyledTableCellItem>
                    </div>
                    <StyledTableCellCategoryIcon>
                        <ContractImage categoryId={contractCategory} />
                    </StyledTableCellCategoryIcon>
                </StyledFlexRow>
            </StyledTableCell>
        </StyledTableRow>
    );
};
export default ContractTableItem;
